import type { LanguageTranslation } from "./translation.types";

export const sv: LanguageTranslation = {
    name: "Svenska",
    code: "sv",

    error: {
        login: "Kan ej logga in",
        logout: "Kan ej logga ut",
        authRefresh: "Problem med inloggningen",
        loadMeasurements: "Kan ej hämta mätningar",
        loadMeasurement: (name) => `Kan ej hämta mätning '${name}'`,
        loadChart: "Kan ej hämta graf",
        closeMessage: "Stäng",
        tooFewRespondents:
            "Följande val har exkluderas på grund av för få respondenter",
    },

    menu: {
        login: "Logga in",
        logout: "Logga ut",
        changeLanguage: "Byt språk",
        changeLanguageTo: (lng) => `Byt språk: ${lng}`,
    },

    measurements: {
        results: "Resultat",
        exportResults: "Exportera resultat",
        measurementsNationalCurrent: "Nationella aktuella mätningar",
        measurementsNationalHistorical: "Nationella historiska mätningar",
        measurementsRegional: "Regionala mätningar",
        measurementsPilot: "Pilotmätningar",
        measurementsUnknown: "Okända mätningar",
    },

    measurement: {
        measurementYear: "Mätningsår",
        publicView: "Publik vy",
        showResults: "Visa resultat",
        downloadResults: "Ladda ned resultat",
        superGraph: "Supergraf",
        freeTextResponses: "Fritextsvar",
        requireLogin: "Åtkomst till dessa resultat kräver inloggning",
        interpretationNoteThat: "Observera att",
        interpretationOptionalSelection:
            "Undersökningen har genomförts med frivilliga urval, vilket innebär att regioner kan sakna resultat för vissa filter.",
        interpretationVariedComposition:
            "Jämförelser mellan verksamheter inte alltid är rättvisande eftersom sammansättningen på patienter varierar, vilket kan påverka resultatet.",
    },

    filters: {
        filters: "Filter",
        organisations: "Organisationer",
        organisationsDesc1: "Organisationer att jämföra med",
        organisationsDesc2:
            "Klicka på kryssruta eller organisationsnamn för att välja en organisation",
        organisationsDesc3:
            "Klicka på högerpil-ikonen för att visa underorganisationer",
        organisationsDesc4:
            "Klicka på soptunne-ikonen för att ta bort ett filterval",
        organisationSearch: "Sök organisationer",
        organisationSearchResult: "Sökresultat",
        multiSelectionCreate: "Skapa flerval",
        multiSelectionSave: "Spara flerval",
        multiSelectionDesc1:
            "Detta är ett flervalsfilter och du kan länka flera alternativ tillsammans",
        multiSelectionDesc2: "Klicka på kedje-ikonen för att skapa ett flerval",
        multiSelectionDesc3: "Välj dina val",
        multiSelectionDesc4:
            "Klicka på kedje-ikonen igen för att spara ditt nya flerval",
        selectAll: "Välj alla",
        selectNone: "Välj ingen",
        selectMyRegion: "Välj min region",
        latest: "Senaste",
        selected: "Valda",
        resetSelected: "Nollställ val",
        removeSelection: (name) => `Ta bort val: ${name}`,
        selectOption: "Välj ett val",
        bookmarkDescription:
            "Kopiera adressen eller skapa ett bokmärke för att återvända till dessa filterval senare",
        all: "Alla",
        basedOnMetadataFilters: "Filter baserade på urvalsdata",
        basedOnQuestionFilters: "Filter baserade på enkätsvar",
        basedOnQuestionDesc:
            "Uppgifter om utskickade enkäter och svarsfrekvens kan inte beräknas för dessa filter",
    },

    // Filter names are already in Swedish from the server
    filter: null,

    stats: {
        dimensionScores: "Dimensionsvärden",
        dimensionScore: "Dimensionsvärde",
        dimensionScoreYAxis: "Dimensionsvärde (Önskat värde högt)",
        dimensionScoreDesc: "Medelvärde av alla frågor inom dimensionen",
        dimensionScoreTotal: "Helhetsupplevelse",
        dimensionScoreTotalYAxis: "Helhetsupplevelse (Önskat värde högt)",
        dimensionScoreTotalParentheses: "Medelvärde dimensionsfrågor",
        dimensionScoreTotalDesc:
            "Medelvärde av alla frågor som ingår i alla dimensioner",
        spiderChart: "Spindeldiagram",
        barChart: "Stapeldiagram",
        tableChart: "Tabell",
        questions: "Frågor",
        questionOverview: "Frågeöversikt",
        questionOverviewAllSeries: "Medelvärde dimensionsfrågor: Alla",
        questionOverviewTotal: "Frågeöversikt dimensionsfrågor",
        dimensionsLink: "Läs mer om dimensionerna här",
        choicesPercentage: "Andel svarsalternativ",
        choicesPercentageYAxis: "Andel svarsalternativ (%)",
        meanValue: "Medelvärde",
        meanValueYAxis: "Medelvärde (Önskat värde högt)",
        numRespondents: "Antal respondenter",
        numRespondentsOfTotal: (num, total) => `${num} av ${total}`,
        mailings: "Utskick",
        mailingsNum: "Utskickade enkäter",
        mailingsYAxis: "Antal (k = 1000)",
        answeredNum: "Besvarade enkäter",
        responseRate: "Svarsfrekvens",
        responseRateYAxis: "Svarsfrekvens (%)",
        basedOnQuestionWarn:
            "kan inte beräknas för filter baserade på enkätsvar",
        showQuestions: "Visa frågor",
    },

    // Dimension names are already in Swedish from the server
    // These are here because we want to display something different from the actual dimension name
    dimensions: {
        Levnadsvanor: "Frågor om levnadsvanor",
        "Om patienten": "Frågor om patienten",
        Övrigt: "Övriga frågor",
    },

    download: {
        filtersDesc: "Filter för nedladdning av statistik och standardrapport.",
        downloadAggregated: "Ladda ned statistik (excel)",
        lowestLevel: "Lägstanivå",
        showUnits: "Visa enheter",
        hideUnits: "Dölj enheter",
        downloadStandardReport: "Ladda ned standardrapport (powerpoint)",
        standardReportDesc1:
            "Det kan ta flera minuter att ladda ned rapporten då mycket data måste räknas ut.",
        standardReportDesc2:
            "Man kan välja Sverige, flera regioner eller flera enheter/vårdcentraler/mottagningar. Det går EJ att kombinera mellan de olika nivåerna.",
        downloadReport: "Ladda ned rapport",
        standardReportSelectedOrg: "Valda organisationer",
        standardReportIncludeHistory: "Inkludera historik",
        standardReportIncludeHistoryTooltip: "Historik inkluderar tidigare år",
        standardReportIncludeQuestions: "Inkludera frågor",
        downloadQuestionnaire: "Ladda ned enkät (excel)",
        selectedQuestionnaire: "Vald enkät",
        downloadRegionalRawData: "Ladda ned regional rådata (csv)",
        includeFreeText: "Inkludera fritextsfrågor",
    },

    freeText: {
        noOrganisationSelected: "Alla",
        filters: "Filter",
        question: "Fråga",
        cantFindAnswers: "Kunde inte hitta några fritextsvar",
        questionAnswers: "Frågesvar",
        numPerPage: "Visa per sida",
        paginatedFreeTextAnswers: "Paginerade fritextsvar",
        showingAnswers: "Visar svar",
        filterFreeTextAnswers: "Filter fritextsvar",
        resultsFreeTextAnswers: "Resultat fritextsvar",
        downloadFreeTextExport: "Ladda ner fritextsvar",
        organisation: "Organisation",
        child: "Barn",
        svf: "Vårdförlopp",
        periodMonth: "Månad",
        periodQuarter: "Kvartal",
        overallScore: "Helhetsintryck",
        categories: "Kategorier",
    },

    pagination: {
        paginationTop: "Paginering topp",
        paginationBottom: "Paginering botten",
        first: "Första",
        last: "Sista",
        previous: "Föregående",
        next: "Nästa",
    },

    months: {
        1: "Januari",
        2: "Februari",
        3: "Mars",
        4: "April",
        5: "Maj",
        6: "Juni",
        7: "Juli",
        8: "Augusti",
        9: "September",
        10: "Oktober",
        11: "November",
        12: "December",
    },

    highcharts: {
        contextButtonTitle: "Exportera diagram",
        downloadPNG: "Ladda ner diagram som PNG",
        downloadJPEG: "Ladda ner diagram som JPEG",
        downloadSVG: "Ladda ner diagram som SVG",
        downloadPDF: "Ladda ner diagram som PDF",
        downloadCSV: "Ladda ner diagram som CSV",
        downloadXLS: "Ladda ner diagram som XLS",
        printChart: "Skriv ut diagram",
        viewFullscreen: "Se i fullskärmsläge",
    },

    misc: {
        back: "Tillbaka",
        help: "Hjälp",
    },
};
